import { z } from 'zod';

const CODE_MIN = 2;
const CODE_MAX = 32;
const CATEGORY_MAX = 32;
const CODE_PATTERN = new RegExp(`^[A-Z0-9_-]{${String(CODE_MIN)},${String(CODE_MAX)}}$`, 'u');
const CATEGORY_PATTERN = new RegExp(`^[A-Z][A-Z0-9_-]{0,${String(CATEGORY_MAX - 1)}}$`, 'u');
const MAX_NAME = 255;
const MAX_DESCRIPTION = 4000;
const MAX_URL = 512;

export const AMENITY_CATEGORY_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'GENERAL', label: 'General' },
  { value: 'ACCESSIBILITY', label: 'Accessibility' },
  { value: 'BATHROOM', label: 'Bathroom' },
  { value: 'BUSINESS', label: 'Business' },
  { value: 'DINING', label: 'Dining' },
  { value: 'ENTERTAINMENT', label: 'Entertainment' },
  { value: 'KITCHEN', label: 'Kitchen' },
  { value: 'WELLNESS', label: 'Wellness' },
];

export const AMENITY_TYPE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'PROPERTY', label: 'Property' },
  { value: 'ROOM', label: 'Room' },
];

export const amenityRow = z.object({
  id: z.string(),
  code: z.string().optional(),
  name: z.string().optional(),
  type: z.string().optional(),
  category: z.string().optional(),
  iconUrl: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export const amenityDetail = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  type: z.enum(['PROPERTY', 'ROOM']),
  category: z.string(),
  iconUrl: z.string().nullable(),
  description: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  version: z.number(),
});

export const amenityFormInput = z.object({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .min(CODE_MIN, `Code must be at least ${String(CODE_MIN)} characters`)
    .max(CODE_MAX, `Code must be ${String(CODE_MAX)} characters or fewer`)
    .regex(
      CODE_PATTERN,
      'Code can only contain uppercase letters, digits, underscores and hyphens',
    ),
  name: z
    .string()
    .trim()
    .min(1, 'A name is required')
    .max(MAX_NAME, `Keep the name under ${String(MAX_NAME)} characters`),
  type: z.enum(['PROPERTY', 'ROOM']),
  category: z
    .string()
    .trim()
    .toUpperCase()
    .min(1, 'A category is required')
    .max(CATEGORY_MAX, `Keep the category under ${String(CATEGORY_MAX)} characters`)
    .regex(
      CATEGORY_PATTERN,
      'Category must be a single uppercase word using letters, digits, underscores and hyphens',
    ),
  iconUrl: z.union([
    z.literal(''),
    z
      .url({ message: 'Enter a full URL, such as https://cdn.example/icon.svg' })
      .max(MAX_URL, `Keep the icon URL under ${String(MAX_URL)} characters`),
  ]),
  description: z
    .string()
    .trim()
    .max(MAX_DESCRIPTION, `Keep the description under ${String(MAX_DESCRIPTION)} characters`),
});

export type AmenityRow = z.infer<typeof amenityRow>;
export type AmenityDetail = z.infer<typeof amenityDetail>;
export type AmenityFormInput = z.infer<typeof amenityFormInput>;
